import { useAuth } from '../context/AuthContext'
import { useGame } from '../context/GameContext'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Slider } from '../components/ui/Slider'
import { Card, CardContent } from '../components/ui/Card'
import { useToast } from '../components/ui/Toaster'
import { 
  ArrowRight, X, Loader2, 
  CheckCircle, Users as UsersIcon,
  AlertCircle
} from 'lucide-react'
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'

export default function DuelGame() {
  const { inviteCode } = useParams<{ inviteCode: string }>()
  const { user } = useAuth()
  const { currentDuel, fetchDuel, submitDuelResponse } = useGame()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [spectrumA, setSpectrumA] = useState(50)
  const [spectrumB, setSpectrumB] = useState(50)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [polling, setPolling] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)

  useEffect(() => {
    if (inviteCode) {
      fetchDuel(inviteCode)
    }
  }, [inviteCode, fetchDuel])

  useEffect(() => {
    if (currentDuel && currentDuel.status === 'completed') {
      navigate(`/duel/${inviteCode}/result`)
    }
  }, [currentDuel, inviteCode, navigate])

  // Poll for completion after submitting
  useEffect(() => {
    if (polling && inviteCode) {
      const pollInterval = setInterval(async () => {
        await fetchDuel(inviteCode)
        if (currentDuel?.status === 'completed') {
          clearInterval(pollInterval)
          setPolling(false)
          navigate(`/duel/${inviteCode}/result`)
        }
      }, 2000)

      // Stop polling after 30 seconds
      setTimeout(() => {
        clearInterval(pollInterval)
        setPolling(false)
      }, 30000)

      return () => clearInterval(pollInterval)
    }
  }, [polling, inviteCode, fetchDuel, currentDuel, navigate])

  // Countdown timer
  useEffect(() => {
    if (currentDuel?.expires_at) {
      const updateTimer = () => {
        const now = new Date()
        const expires = new Date(currentDuel.expires_at)
        const diff = expires.getTime() - now.getTime()
        
        if (diff <= 0) {
          setTimeRemaining(0)
        } else {
          setTimeRemaining(Math.floor(diff / 1000))
        }
      }
      
      updateTimer()
      const timer = setInterval(updateTimer, 1000)
      return () => clearInterval(timer)
    }
  }, [currentDuel])

  const isCreator = Boolean(user && currentDuel?.creator_id === user.id)
  const isOpponent = Boolean(user && currentDuel?.opponent_id === user.id)

  // Extract values to local variables first, then check
  const creatorVal = currentDuel?.creator_spectrum_a
  const opponentVal = currentDuel?.opponent_spectrum_a
  
  // Explicit boolean type with explicit check - wrap in Boolean() to satisfy TypeScript
  const creatorSubmitted: boolean = Boolean(creatorVal !== null && creatorVal !== undefined)
  const opponentSubmitted: boolean = Boolean(opponentVal !== null && opponentVal !== undefined)
  const currentUserSubmitted = isCreator ? creatorSubmitted : opponentSubmitted

  const handleSubmit = async () => {
    if (!currentDuel || !user) return

    setSubmitting(true)
    try {
      const { error } = await submitDuelResponse(currentDuel.id, spectrumA, spectrumB)
      if (error) throw error

      setSubmitted(true)
      setPolling(true)
      toast({ type: 'success', title: 'Submitted!', message: 'Waiting for the other player...' })
    } catch (err) {
      toast({ type: 'error', title: 'Failed to submit', message: err instanceof Error ? err.message : 'Try again' })
    } finally {
      setSubmitting(false)
    }
  }

  if (!currentDuel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
        <Card className="max-w-md w-full text-center p-8">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-surface-600 dark:text-surface-400">Loading duel...</p>
        </Card>
      </div>
    )
  }

  const prompt = currentDuel.prompt
  if (!prompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
        <Card className="max-w-md w-full text-center p-8">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-2">Prompt not found</h2>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </Card>
      </div>
    )
  }

  const crowdAAvg = currentDuel.crowd_a_avg ?? prompt.crowd_a_avg ?? 50
  const crowdBAvg = currentDuel.crowd_b_avg ?? prompt.crowd_b_avg ?? 50

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-surface-500 dark:text-surface-400">Duel Mode</p>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100">
            {prompt.spectrum_a_label} + {prompt.spectrum_b_label}
          </h1>
        </div>
        <div className="flex items-center gap-2 text-sm text-surface-500 dark:text-surface-400">
          <UsersIcon className="w-4 h-4" />
          <span>Code: {currentDuel.invite_code}</span>
          {timeRemaining > 0 && (
            <span className="text-brand-600 dark:text-brand-400 font-mono">{formatTime(timeRemaining)}</span>
          )}
        </div>
      </div>

      {/* Player Status */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 gap-4">
            <PlayerCard
              name={currentDuel.creator_username || 'Creator'}
              isYou={isCreator}
              submitted={creatorSubmitted}
              avatar={currentDuel.creator_username?.[0]?.toUpperCase() || 'C'}
              color="brand"
            />
            <PlayerCard
              name={currentDuel.opponent_username || 'Waiting...'}
              isYou={isOpponent}
              submitted={opponentSubmitted}
              avatar={currentDuel.opponent_username?.[0]?.toUpperCase() || '?'}
              color="purple"
              waiting={!currentDuel.opponent_id}
            />
          </div>
        </CardContent>
      </Card>

      {/* Spectrum A */}
      <Card>
        <CardContent className="pt-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100">
                {prompt.spectrum_a_label}
              </h3>
              <span className="text-sm text-surface-500 dark:text-surface-400">
                Crowd avg: {crowdAAvg}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm text-surface-500 dark:text-surface-400 mb-3">
              <span>{prompt.spectrum_a_left}</span>
              <span>{prompt.spectrum_a_right}</span>
            </div>
            <Slider
              value={spectrumA}
              onChange={(val) => setSpectrumA(val)}
              min={0}
              max={100}
              step={1}
              leftLabel={prompt.spectrum_a_left}
              rightLabel={prompt.spectrum_a_right}
              disabled={currentUserSubmitted || submitted}
              showValue={true}
            />
          </div>
        </CardContent>
      </Card>

      {/* Divider */}
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-dashed border-surface-200 dark:border-surface-700" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white dark:bg-surface-900 px-3 text-surface-400">OR</span>
        </div>
      </div>

      {/* Spectrum B */}
      <Card>
        <CardContent className="pt-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100">
                {prompt.spectrum_b_label}
              </h3>
              <span className="text-sm text-surface-500 dark:text-surface-400">
                Crowd avg: {crowdBAvg}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm text-surface-500 dark:text-surface-400 mb-3">
              <span>{prompt.spectrum_b_left}</span>
              <span>{prompt.spectrum_b_right}</span>
            </div>
            <Slider
              value={spectrumB}
              onChange={(val) => setSpectrumB(val)}
              min={0}
              max={100}
              step={1}
              leftLabel={prompt.spectrum_b_left}
              rightLabel={prompt.spectrum_b_right}
              disabled={currentUserSubmitted || submitted}
              showValue={true}
            />
          </div>
        </CardContent>
      </Card>

      {/* Submit / Waiting */}
      {submitted || currentUserSubmitted ? (
        <Card className="bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/30 dark:to-brand-800/30 border-brand-200 dark:border-brand-800">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-brand-500 rounded-full flex items-center justify-center animate-pulse">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-brand-900 dark:text-brand-100 mb-2">
              {submitted ? 'Submitted!' : 'Already Answered'}
            </h3>
            <p className="text-brand-700 dark:text-brand-300 mb-4">
              {submitted 
                ? 'Waiting for the other player to answer...' 
                : 'You\'ve already submitted your answers for this duel.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="secondary" onClick={() => navigate('/')}>
                <X className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          className="w-full"
          size="lg"
          onClick={handleSubmit}
          loading={submitting}
        >
          Submit & See Sync <ArrowRight className="w-4 h-4" />
        </Button>
      )}
    </div>
  )
}

interface PlayerCardProps {
  name: string
  isYou: boolean
  submitted: boolean
  avatar: string
  color: 'brand' | 'purple'
  waiting?: boolean
}

function PlayerCard({ name, isYou, submitted, avatar, color, waiting }: PlayerCardProps) {
  const colors = {
    brand: 'bg-gradient-to-br from-brand-500 to-brand-600',
    purple: 'bg-gradient-to-br from-purple-500 to-purple-600',
  }

  return (
    <div className={clsx(
      'p-4 rounded-xl text-center relative',
      isYou ? 'ring-2 ring-brand-500 bg-brand-50 dark:bg-brand-900/20' : 'bg-surface-50 dark:bg-surface-900/50'
    )}>
      {isYou && (
        <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-brand-500 text-white text-xs rounded-full font-medium">
          You
        </span>
      )}
      {waiting && (
        <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full font-medium">
          Waiting
        </span>
      )}
      <div className={clsx('w-16 h-16 mx-auto mb-3 rounded-xl flex items-center justify-center text-white font-bold text-2xl', colors[color])}>
        {avatar}
      </div>
      <p className="font-semibold text-surface-900 dark:text-surface-100">{name}</p>
      <p className={clsx('text-sm mt-1 flex items-center justify-center gap-1', submitted ? 'text-green-600' : 'text-surface-500')}>
        {submitted ? <CheckCircle className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {submitted ? 'Submitted' : waiting ? 'Waiting...' : 'Waiting...'}
      </p>
    </div>
  )
}